import React, { FC, useMemo, ReactNode } from "react";
import useWatchState from "../../../../hooks/useWatchState";

export interface IRadioGroupContext {
  value: string;
  name: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const RadioGroupContext = React.createContext<IRadioGroupContext>({
  value: null,
  name: "",
  onChange: null,
});

export interface IRadioGroupProps {
  /**
   * Set initial selected radio in group
   */
  selectedValue?: string;
  /**
   * Set name attribute for all radio inputs in group
   */
  name?: string;
  /**
   * A callback function for when this component is changing input state
   */
  children?: ReactNode;
  onChange?(event: React.ChangeEvent<HTMLInputElement>): void;
}

const RadioGroup: FC<IRadioGroupProps> = ({
  selectedValue,
  name,
  children,
  onChange,
}) => {
  const [valueState, setSelectedValue] = useWatchState<string>({
    value: selectedValue,
  });

  const context = useMemo(
    () => ({
      value: valueState,
      name,
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        setSelectedValue(event.target.value);
        onChange && onChange(event);
      },
    }),
    [name, onChange, setSelectedValue, valueState],
  );

  return (
    <RadioGroupContext.Provider value={context}>
      {children}
    </RadioGroupContext.Provider>
  );
};

export default RadioGroup;
